import {
  Box,
  Divider,
  HStack,
  Link,
  SlideFade,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { endOfToday, formatISO, startOfDay, subDays } from "date-fns";
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { FaRedo, FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { listSalesforceSyncErrorsApi } from "../../../../../common/api/integrations/salesforce";
import {
  isFulfilled,
  isLoading,
} from "../../../../../common/helper/commonHelper";
import {
  SalesforceConnection,
  SalesforceSyncError,
} from "../../../../../common/types/connection";
import { DataTable } from "../../../../../components/data-table/DataTable";
import DateTimeRangeFilter, {
  FormatDate,
} from "../../../../../components/DateTimeRangeFilter";
import IButton from "../../../../../components/IButton";
import SpinnerContainer from "../../../../../components/SpinnerContainer";
import TableEntryText from "../../../../../components/TableEntryText";
import { useAppDispatch } from "../../../../../store";
import SubHeader from "../../components/SubHeader";
import {
  archiveSalesforceSyncError,
  listSalesforceSyncErrors,
  listSalesforceSyncErrorsTypes,
  resyncSalesforceSyncError,
  selectConnection,
} from "../../connectionSlice";
import SalesforceAffectedRecordsModal from "./SalesforceAffectedRecordsModal";

const SalesforceSyncErrors = forwardRef(
  (
    {
      connectionDetails,
    }: {
      connectionDetails: SalesforceConnection;
    },
    ref
  ) => {
    const pageSize = 10;
    let totalErrorCount = 0;
    const dispatch = useAppDispatch();
    const {
      salesforce: {
        syncErrors,
        sfErrorTypes,
        savingErrorDetails,
        resyncingSalesforceSyncError,
        archivingSalesforceSyncError,
      },
    } = useSelector(selectConnection);

    const [[startTime, endTime], setDateRange] = useState([
      startOfDay(subDays(new Date(), 7)),
      endOfToday(),
    ]);
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedRows, setSelectedRows] = useState<SalesforceSyncError[]>([]);

    function onDateChange(startTime: Date, endTime: Date) {
      setDateRange([startTime, endTime]);
    }

    const connectionId = connectionDetails.connection_id;

    const lastSearchedDate = useRef({ startTime, endTime });
    const [selectedRow, setSelectedRow] = useState<SalesforceSyncError | null>(
      null
    );

    const {
      isOpen: isOpenAffectedRecordsModal,
      onOpen: onOpenAffectedRecordsModal,
      onClose: onCloseAffectedRecordsModal,
    } = useDisclosure();

    const {
      isOpen: isOpenArchiveResyncTab,
      onOpen: onOpenArchiveResyncTab,
      onClose: onCloseArchiveResyncTab,
    } = useDisclosure();

    function dateFilterHandler(startTime: Date, endTime: Date) {
      if (connectionId && sfErrorTypes.data) {
        sfErrorTypes.data.forEach((key, value) => {
          dispatch(
            listSalesforceSyncErrors({
              connectionId,
              errorType: key.name,
              startTime: formatISO(startTime),
              endTime: formatISO(endTime),
              pageSize: pageSize,
              pageNumber: 1,
            })
          );
        });
        lastSearchedDate.current = { startTime, endTime };
      }
    }

    useImperativeHandle(ref, () => ({
      filterWithDate(date: Date) {
        const startDate = date;
        const endDate = new Date();
        dateFilterHandler(startDate, endDate);
        setDateRange([startDate, endDate]);
      },
    }));

    useEffect(() => {
      dispatch(listSalesforceSyncErrorsTypes());
    }, [dispatch]);

    function formatText(value: string | undefined) {
      return <TableEntryText>{value ?? "-"}</TableEntryText>;
    }

    function formatCount(value: number) {
      return `${value} record${value > 1 ? "s" : ""}`;
    }

    const handleOpenAffectedRecordsModal = useCallback(
      (row: SalesforceSyncError) => {
        setSelectedRow(row);
        onOpenAffectedRecordsModal();
      },
      [onOpenAffectedRecordsModal]
    );

    function closeAffectedRecordsModal() {
      setSelectedRow(null);
      onCloseAffectedRecordsModal();
    }

    const columnHelper = createColumnHelper<SalesforceSyncError>();

    const columns = useMemo(
      () => [
        columnHelper.accessor("error_class", {
          header: "Error Class",
          size: 500,
          cell: (value) => formatText(value.getValue()),
        }),
        columnHelper.accessor("count", {
          header: "Affected Records",
          size: 100,
          cell: (info) => (
            <Link
              onClick={() => handleOpenAffectedRecordsModal(info.row.original)}
            >
              <Text color="blue.600" as="u">
                {formatCount(info.getValue())}
              </Text>
            </Link>
          ),
          meta: {
            isNumeric: true,
          },
        }),
        columnHelper.accessor("first_occurred", {
          header: "First Detected",
          size: 100,
          cell: (info) => (
            <FormatDate date={info.getValue()} showTime splitLines />
          ),
        }),
        columnHelper.accessor("last_occurred", {
          header: "Last Detected",
          size: 100,
          cell: (info) => (
            <FormatDate date={info.getValue()} showTime splitLines />
          ),
        }),
      ],
      [columnHelper, handleOpenAffectedRecordsModal]
    );

    function switchTabs(value: number) {
      setTabIndex(value);
    }

    let totalErrorCountRef = useRef(totalErrorCount);

    const isResyncing: boolean = useMemo(
      () => isLoading(resyncingSalesforceSyncError),
      [resyncingSalesforceSyncError]
    );

    const isArchiving = useMemo(
      () => isLoading(archivingSalesforceSyncError),
      [archivingSalesforceSyncError]
    );

    useEffect(() => {
      if (sfErrorTypes.data) {
        totalErrorCountRef.current = 0;
        sfErrorTypes.data.map(async (key, value) => {
          const result = await dispatch(
            listSalesforceSyncErrors({
              connectionId,
              errorType: sfErrorTypes.data[value].name,
              startTime: formatISO(lastSearchedDate.current.startTime),
              endTime: formatISO(lastSearchedDate.current.endTime),
              pageSize: pageSize,
              pageNumber: 1,
            })
          );
          if (isFulfilled(result.meta.requestStatus)) {
            const newSfSyncError = result.payload as Awaited<
              ReturnType<typeof listSalesforceSyncErrorsApi>
            >;
            totalErrorCountRef.current += newSfSyncError.record_count;
          }
        });
      }
    }, [sfErrorTypes.data, connectionId, dispatch, isResyncing, isArchiving]);

    useEffect(() => {
      if (isArchiving) {
        setSelectedRows([]);
      }
    }, [isArchiving]);

    useEffect(() => {
      if (isResyncing) {
        setSelectedRows([]);
      }
    }, [isResyncing]);

    const [requiredErrorType, setRequiredErrorType] = useState<
      string | undefined
    >("");
    const [requiredErrorDisplay, setRequiredErrorDisplay] = useState<
      string | undefined
    >("");

    useEffect(() => {
      if (sfErrorTypes.data[tabIndex]) {
        setRequiredErrorType(sfErrorTypes.data[tabIndex].name);
        setRequiredErrorDisplay(sfErrorTypes.data[tabIndex].display);
      }
    }, [sfErrorTypes.data, tabIndex]);

    useEffect(() => {
      if (selectedRows.length === 0) {
        onCloseArchiveResyncTab();
      } else {
        onOpenArchiveResyncTab();
      }
    }, [selectedRows, onCloseArchiveResyncTab, onOpenArchiveResyncTab]);

    function wrapErrorData() {
      const errorClassList = selectedRows.map((value) => {
        return value.error_class;
      });
      const wrappedData = {
        errorClassList,
        startTime: formatISO(startTime),
        endTime: formatISO(endTime),
      };
      return wrappedData;
    }

    function handleResyncError() {
      const resyncData = wrapErrorData();
      dispatch(resyncSalesforceSyncError(resyncData));
    }

    function handleArchiveError() {
      const archiveData = wrapErrorData();
      dispatch(archiveSalesforceSyncError(archiveData));
    }

    function ResyncArchiveTab() {
      return (
        <SlideFade in={isOpenArchiveResyncTab}>
          <HStack spacing="20px" bgColor="gray.100" py="2">
            <Box pl="20px">
              <Text fontSize="sm">
                {selectedRows.length === 0
                  ? null
                  : `${selectedRows.length} selected`}
              </Text>
            </Box>
            <IButton
              size="sm"
              aria-label="Resync"
              disabled={selectedRows.length === 0}
              onClick={handleResyncError}
              isLoading={isLoading(resyncingSalesforceSyncError)}
              leftIcon={<FaRedo fontSize="sm" />}
            >
              Resync
            </IButton>
            <IButton
              size="sm"
              aria-label="Archive"
              disabled={selectedRows.length === 0}
              onClick={handleArchiveError}
              isLoading={isLoading(archivingSalesforceSyncError)}
              leftIcon={<FaTrash fontSize="sm" />}
            >
              Archive
            </IButton>
          </HStack>
        </SlideFade>
      );
    }

    return (
      <Box p="5" bg="white" rounded="md">
        <SubHeader title={`Sync errors (${totalErrorCountRef.current})`}>
          <DateTimeRangeFilter
            startDate={startTime}
            endDate={endTime}
            onDateChange={onDateChange}
            dateFilterHandler={() => dateFilterHandler(startTime, endTime)}
          />
        </SubHeader>
        <Tabs p={0} tabIndex={tabIndex}>
          <TabList borderBottom={1}>
            {sfErrorTypes.data.map((key, value) => {
              return (
                <Tab fontSize="sm" onClick={() => switchTabs(value)}>
                  {key.display} ({syncErrors[key.name]?.count ?? 0})
                </Tab>
              );
            })}
          </TabList>
          <Divider pt="3px" />
          <SpinnerContainer loading={isLoading(savingErrorDetails)}>
            {requiredErrorType && syncErrors[requiredErrorType] && (
              <>
                <ResyncArchiveTab />
                <DataTable
                  loading={isLoading(savingErrorDetails)}
                  list={syncErrors[requiredErrorType].list}
                  setPage={(pageNo: number) =>
                    listSalesforceSyncErrors({
                      connectionId,
                      errorType: requiredErrorType as string,
                      startTime: formatISO(lastSearchedDate.current.startTime),
                      endTime: formatISO(lastSearchedDate.current.endTime),
                      pageSize: pageSize,
                      pageNumber: pageNo,
                    })
                  }
                  totalPageCount={syncErrors[requiredErrorType].totalPageCount}
                  currentPage={syncErrors[requiredErrorType].currentPageNo ?? 1}
                  columns={columns}
                  emptyMsg="No sync errors found"
                  onSelectionChange={setSelectedRows}
                />
              </>
            )}
          </SpinnerContainer>
          <SalesforceAffectedRecordsModal
            isOpen={isOpenAffectedRecordsModal}
            onClose={closeAffectedRecordsModal}
            errorType={requiredErrorDisplay ?? ""}
            data={selectedRow}
          />
        </Tabs>
      </Box>
    );
  }
);

export default SalesforceSyncErrors;
